<script setup lang="ts">
import { computed, ref, toRaw, watch } from 'vue'
import {
  lockNotificationRecipientsError,
  lockNotificationSenderError,
  parseRecipients,
  type VatLockNotification,
} from '@/utils/vatNotification'

// Switch for the lock e-mail of one category; sender and recipients appear once it is on.
const notification = defineModel<VatLockNotification>({ required: true })

withDefaults(
  defineProps<{
    label?: string
    description?: string
    disabled?: boolean
  }>(),
  {
    label: 'E-Mail bei Sperrung',
    description: '',
    disabled: false,
  }
)

const emit = defineEmits<{ dirty: [] }>()

let lastUpdate: VatLockNotification | undefined
const update = (patch: Partial<VatLockNotification>) => {
  lastUpdate = { ...notification.value, ...patch }
  notification.value = lastUpdate
  emit('dirty')
}

// Raw input keeps the user's separators while typing; the model holds the parsed list. It is dropped when the
// settings are replaced from outside, e.g. after reloading the configuration.
const recipientsInput = ref<string | null>(null)
watch(notification, (next) => {
  if (toRaw(next) !== lastUpdate) {
    recipientsInput.value = null
  }
})

const recipientsText = computed({
  get: () => recipientsInput.value ?? (notification.value.recipients ?? []).join(', '),
  set: (value: string) => {
    recipientsInput.value = value
    update({ recipients: parseRecipients(value) })
  },
})

const sender = computed({
  get: () => notification.value.sender ?? '',
  set: (value: string) => update({ sender: value.trim() }),
})

const senderError = computed(() => lockNotificationSenderError(notification.value))
const recipientsError = computed(() => lockNotificationRecipientsError(notification.value))

defineExpose({ recipientsText, sender, senderError, recipientsError })
</script>

<template>
  <div class="space-y-3">
    <div class="flex items-start justify-between gap-4">
      <div class="space-y-0.5">
        <p class="text-sm font-medium text-gray-900 dark:text-white">
          {{ label }}
        </p>
        <p v-if="description" class="text-xs text-muted leading-relaxed">
          {{ description }}
        </p>
      </div>
      <USwitch
        :model-value="notification.enabled"
        :disabled="disabled"
        class="shrink-0 pt-0.5"
        data-test="lock-notification-switch"
        @update:model-value="(value: boolean) => update({ enabled: value })"
      />
    </div>

    <div v-if="notification.enabled" class="space-y-3" data-test="lock-notification-fields">
      <UFormField
        label="Absender (optional)"
        description="Leer lassen, um den Standard-Absender des Mandanten zu verwenden."
        :error="senderError"
      >
        <UInput
          v-model="sender"
          type="email"
          :disabled="disabled"
          placeholder="ustid@firma.de"
          class="w-full"
        />
      </UFormField>
      <UFormField
        label="Empfänger"
        description="Mehrere E-Mail-Adressen mit Komma oder Semikolon trennen."
        :error="recipientsError"
      >
        <UInput
          v-model="recipientsText"
          :disabled="disabled"
          placeholder="buchhaltung@firma.de, vertrieb@firma.de"
          class="w-full"
        />
      </UFormField>
    </div>
  </div>
</template>
